import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Trash2, PlusCircle, X as XIcon, ArrowUpAZ, ArrowDownZA, ArrowUpDown } from "lucide-react";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import { useTheme } from "../../../hooks/useTheme";
import Pagination from "../../../components/shared_components/Pagination";
import Loader from "../../../components/Loader";
import SequenceCreatorModal from "./SequenceCreatorModal";
import { isCombinedServerStep } from "./builder/sequenceBuilderApi";

const NEW_BUILDER_ROUTE = "/org-admin/sourcing/sequences/new";
const editBuilderRoute = (id) => `/org-admin/sourcing/sequences/edit/${id}`;

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;
const GLOBAL_TEMPLATE_ID = "0de1b509-f77a-44ab-82cf-25a03755ca97";

function fmtDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch { return ""; }
}

const SequenceManager = () => {
    const orgId = useSelector(state => state.userSlice?.org_id);
    const recruiterId = useSelector(state => state.userSlice?.org_admin_id);
    const navigate = useNavigate();
    const location = useLocation();

    const [sequences, setSequences] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [steps, setSteps] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [error, setError] = useState(null);

    const [globalTemplate, setGlobalTemplate] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [toast, setToast] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    // id of the row whose editor is being opened — see openSequence.
    const [openingId, setOpeningId] = useState(null);

    // Search / sort / paging are all client-side: the list endpoint returns every sequence
    // for the org in one call, so there's nothing to round-trip for.
    const { theme } = useTheme();
    const [searchQuery, setSearchQuery] = useState("");
    const [sortOrder, setSortOrder] = useState(null); // null | "A-Z" | "Z-A"
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const visibleSequences = useMemo(() => {
        const term = searchQuery.trim().toLowerCase();
        const filtered = term
            ? sequences.filter(s => (s.name || "").toLowerCase().includes(term))
            : sequences;
        if (!sortOrder) return filtered;
        return [...filtered].sort((a, b) => {
            const cmp = (a.name || "").localeCompare(b.name || "");
            return sortOrder === "A-Z" ? cmp : -cmp;
        });
    }, [sequences, searchQuery, sortOrder]);

    const totalItems = visibleSequences.length;
    const pageSequences = visibleSequences.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Filtering can leave the current page past the end of the list.
    useEffect(() => { setCurrentPage(1); }, [searchQuery, sortOrder]);

    const handleSortName = () =>
        setSortOrder(prev => (prev === "A-Z" ? "Z-A" : prev === "Z-A" ? null : "A-Z"));

    const showToast = (msg) => {
        setToast(msg);
        setTimeout(() => setToast(null), 3500);
    };

    // Surface the "created" toast handed back by the new sequence builder, then clear the
    // router state so a refresh doesn't re-show it.
    useEffect(() => {
        if (location.state?.toast) {
            showToast(location.state.toast);
            navigate(location.pathname, { replace: true });
        }
    }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        // orgId resolves asynchronously on page load (Header dispatches the
        // confirmed org id from /update_last_login after mount). Fetching before
        // it's known sends org_id=undefined, which the backend rejects with 422.
        if (!orgId) return;

        const controller = new AbortController();
        setError(null);
        setLoading(true);
        Promise.allSettled([
            axios.get(`${SOURCING_URL}/email/sequences`, { params: { org_id: orgId }, signal: controller.signal }),
            axios.get(`${SOURCING_URL}/email/templates`, { params: { org_id: orgId, created_by: recruiterId }, signal: controller.signal }),
            axios.get(`${SOURCING_URL}/email/global-templates`, { signal: controller.signal }),
        ])
            .then(([seqRes, tmplRes, globalRes]) => {
                if (controller.signal.aborted) return;
                if (seqRes.status === "fulfilled") setSequences(seqRes.value.data);
                else setError("Failed to load outreach.");
                if (tmplRes.status === "fulfilled") setTemplates(tmplRes.value.data);
                const found = globalRes.status === "fulfilled"
                    ? (globalRes.value.data.find(t => t.id === GLOBAL_TEMPLATE_ID) || null)
                    : null;
                setGlobalTemplate(found);
            })
            .finally(() => { if (!controller.signal.aborted) setLoading(false); });
        return () => controller.abort();
    }, [orgId, recruiterId]);

    const loadSteps = async (seqId) => {
        const { data } = await axios.get(`${SOURCING_URL}/email/sequences/${seqId}/steps`);
        setSteps(data);
        return data;
    };

    const loadTemplates = async () => {
        const { data } = await axios.get(`${SOURCING_URL}/email/templates`, { params: { org_id: orgId, created_by: recruiterId } });
        setTemplates(data);
    };

    // Open a sequence for editing. Every outreach goes to the wizard — the same one it was
    // created in, which handles email, WhatsApp and AI call steps — except one that still has a
    // combined Email + WhatsApp step from the classic modal. The wizard has one channel per
    // step and would show that step as WhatsApp alone, so those keep the classic modal. Steps
    // are loaded either way — that check needs them, and the modal takes them as editSteps.
    //
    // That steps call has to finish before anything can move, so the click is unavoidably
    // async. openingId is what makes the wait visible: without it the row absorbs the click
    // and nothing on screen changes until the response lands, which reads as a dead button.
    // It also blocks a second click from opening a competing editor mid-flight.
    const openSequence = async (seq) => {
        if (openingId) return;
        setOpeningId(seq.id);
        try {
            const stepsData = await loadSteps(seq.id);
            if (!stepsData.some(isCombinedServerStep)) {
                navigate(editBuilderRoute(seq.id));
                return;
            }
            setSelected(seq);
            setShowEditModal(true);
        } catch {
            // Previously this rejected unhandled and the row simply stayed inert.
            showToast("Couldn't open this outreach. Please try again.");
        } finally {
            setOpeningId(null);
        }
    };

    const initiateDelete = async (e, seq) => {
        e.stopPropagation();
        const { data } = await axios.get(`${SOURCING_URL}/email/sequences/${seq.id}/pending-count`);
        setDeleteTarget({ seq, pendingCount: data.pending_emails });
    };

    const confirmDelete = async () => {
        setDeleteLoading(true);
        await axios.delete(`${SOURCING_URL}/email/sequences/${deleteTarget.seq.id}`);
        setSequences(prev => prev.filter(s => s.id !== deleteTarget.seq.id));
        if (selected?.id === deleteTarget.seq.id) setSelected(null);
        setDeleteTarget(null);
        setDeleteLoading(false);
        showToast("Outreach deleted");
    };

    return (
        <>
        {toast && (
            <div className="fixed top-5 right-5 z-[100] flex items-center gap-2 px-4 py-3 dark:bg-[#1B1524] bg-bgPrimary rounded-xl shadow-lg text-sm border border-green-200 dark:border-green-500/30 text-green-700 dark:text-green-400">
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {toast}
            </div>
        )}
            {/* Mirrors the created-JDs list (shared_components/ViewCreatedJDs): same card shell,
                table-container / table-header chrome, responsive Table and shared Pagination. */}
            <div className="min-h-screen flex flex-col border rounded-2xl border-gray-700/50 dark:glass-effect bg-bgSecondary">
                <div className="flex flex-col flex-1 w-full justify-center items-center">
                    <div
                        className="table-container flex-1 w-full"
                        style={{ transform: "translateZ(0)", backfaceVisibility: "hidden", contain: "layout" }}
                    >
                        <div className="table-header px-6">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 py-4 mb-4">
                                <div className="flex items-center gap-4">
                                    <h1 className="text-xl dark:text-[#D9D9D9] text-textPrimary font-normal text-left pl-6">
                                        Outreach
                                    </h1>
                                </div>
                                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder=" Search by Outreach Name"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className={`w-56 ${theme === "dark" ? "glass-effect text-[#EDEDEDE5]" : "bg-bgPrimary !text-textPrimary"} border border-gray-600 rounded-full pl-10 pr-4 placeholder-gray-500 focus:outline-none focus:border-gray-400 dark:focus:border-white text-sm h-10 placeholder:text-sm`}
                                        />
                                        {searchQuery && (
                                            <button onClick={() => setSearchQuery("")}>
                                                <XIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 dark:text-gray-400 text-textSecondary
                                                 dark:hover:text-white hover:text-textPrimary cursor-pointer" />
                                            </button>
                                        )}
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                                        <button
                                            onClick={() => navigate(NEW_BUILDER_ROUTE)}
                                            className="button flex items-center justify-center gap-2 w-full sm:w-auto"
                                        >
                                            <span className="flex gap-2">
                                                <PlusCircle className="w-5 h-5" />
                                                <span className="hidden sm:inline">Create Outreach</span>
                                                <span className="sm:hidden">Create</span>
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Inside table-header so the table picks up the same px-6 as the
                                title — otherwise the first column starts left of the heading. */}
                            <div className="w-full overflow-hidden flex flex-col flex-1 min-h-0">
                                    <div className="w-full flex-1 overflow-y-auto pb-32 min-h-[400px]">
                                    {loading ? (
                                        <div className="flex justify-center py-24">
                                            <Loader />
                                        </div>
                                    ) : error ? (
                                        <p className="text-sm text-red-500 dark:text-red-400 text-center py-24">{error}</p>
                                    ) : (
                                        <Table className="w-full" style={{ transform: "translateZ(0)", contain: "layout" }}>
                                            <Thead className="border-b border-t border-[#5A4479D4]">
                                                <Tr>
                                                    {/* Column widths must be inline styles: HRPortal.css sets
                                                        `.table-container th/td { width: auto }`, which outranks any
                                                        Tailwind width class. Each `tr` is its own `display: table`
                                                        with `table-layout: fixed`, so the two right-hand columns are
                                                        pinned to a fixed width on every row and the unsized name
                                                        column absorbs the rest — pushing them to the right edge. */}
                                                    <Th className="text-left min-w-0">
                                                        <div className="flex items-center dark:text-[#D8D8D8] text-textSecondary text-xs font-medium normal-case">
                                                            Outreach Name
                                                            <button onClick={handleSortName} className="ml-2 focus:outline-none">
                                                                {sortOrder === "A-Z" ? (
                                                                    <ArrowUpAZ className="w-4 h-4 dark:text-gray-300 text-textSecondary" />
                                                                ) : sortOrder === "Z-A" ? (
                                                                    <ArrowDownZA className="w-4 h-4 dark:text-gray-300 text-textSecondary" />
                                                                ) : (
                                                                    <ArrowUpDown className="w-4 h-4 dark:text-gray-300 text-textSecondary" />
                                                                )}
                                                            </button>
                                                        </div>
                                                    </Th>
                                                    <Th className="text-left min-w-0 whitespace-nowrap hidden sm:table-cell" style={{ width: "160px" }}>
                                                        <span className="dark:text-[#D8D8D8] text-textSecondary text-xs font-medium normal-case">
                                                            Created On
                                                        </span>
                                                    </Th>
                                                    <Th className="text-left min-w-0 whitespace-nowrap" style={{ width: "110px" }}>
                                                        <span className="dark:text-[#D8D8D8] text-textSecondary text-xs font-medium normal-case">
                                                            Action
                                                        </span>
                                                    </Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {pageSequences.length === 0 && (
                                                    <Tr>
                                                        <Td colSpan={3}>
                                                            <div className="flex h-60 items-center justify-center">
                                                                <div className="flex flex-col items-center text-center">
                                                                    <h2 className="text-lg font-medium text-gray-400 dark:text-gray-500 mb-1">
                                                                        No outreach found
                                                                    </h2>
                                                                    <p className="text-sm text-gray-400 dark:text-gray-500">
                                                                        {sequences.length === 0
                                                                            ? "Create an outreach to start reaching out to candidates."
                                                                            : "Try adjusting your search terms to see more results."}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </Td>
                                                    </Tr>
                                                )}
                                                {pageSequences.map((seq) => (
                                                    <Tr
                                                        key={seq.id}
                                                        className={`group dark:hover:bg-gray-700/20 hover:bg-gray-300/20 ${
                                                            openingId ? "cursor-wait" : "cursor-pointer"
                                                        }`}
                                                        aria-busy={openingId === seq.id}
                                                        onClick={() => openSequence(seq)}
                                                    >
                                                        <Td className="text-left px-2 py-3 min-w-0">
                                                            <div
                                                                className="flex items-center gap-2 px-2 dark:text-[#CCCCCCE5] text-textSecondary text-sm"
                                                                title={seq.name}
                                                            >
                                                                <span className="whitespace-normal break-words text-left min-w-0">
                                                                    {seq.name}
                                                                </span>
                                                                {/* Inherits the row's text colour rather than hardcoding one, so it
                                                                    reads correctly in both themes. Sized to the row — the full-page
                                                                    Loader is 50px and would stretch it. */}
                                                                {openingId === seq.id && (
                                                                    <span
                                                                        className="inline-block w-3.5 h-3.5 shrink-0 rounded-full border-2 border-current border-t-transparent animate-spin"
                                                                        aria-hidden="true"
                                                                    />
                                                                )}
                                                            </div>
                                                            {seq.description && (
                                                                <div className="truncate text-left px-2 text-xs dark:text-gray-400 text-textSecondary mt-1">
                                                                    {seq.description}
                                                                </div>
                                                            )}
                                                            <div className="text-xs dark:text-gray-400 text-textSecondary mt-1 px-2 sm:hidden">
                                                                {fmtDate(seq.created_at)}
                                                            </div>
                                                        </Td>
                                                        <Td className="text-left px-2 py-3 hidden sm:table-cell min-w-0 whitespace-nowrap" style={{ width: "160px" }}>
                                                            <div className="truncate text-left px-2 dark:text-[#CCCCCCE5] text-textSecondary text-sm">
                                                                {fmtDate(seq.created_at)}
                                                            </div>
                                                        </Td>
                                                        <Td className="text-left px-2 py-3 min-w-0 whitespace-nowrap" style={{ width: "110px" }} onClick={(e) => e.stopPropagation()}>
                                                            {/* px-2 matches the other cells' inner padding; -ml-2 cancels the
                                                                button's own p-2 so the icon sits under the "Action" heading. */}
                                                            <div className="flex items-center px-2">
                                                                <button
                                                                    onClick={(e) => initiateDelete(e, seq)}
                                                                    className="-ml-2 p-2 rounded-full dark:text-gray-400 text-textSecondary hover:text-red-500 dark:hover:bg-gray-700/50 hover:bg-gray-200 transition-colors"
                                                                    title="Delete outreach"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    )}
                                </div>

                                <Pagination
                                    currentPage={currentPage}
                                    totalItems={totalItems}
                                    itemsPerPage={itemsPerPage}
                                    onPageChange={setCurrentPage}
                                    onItemsPerPageChange={(size) => { setItemsPerPage(size); setCurrentPage(1); }}
                                    label="Outreach"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <SequenceCreatorModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                globalTemplate={globalTemplate}
                orgId={orgId}
                recruiterId={recruiterId}
                onSave={(seq) => {
                    setSequences(prev => [seq, ...prev]);
                    setShowModal(false);
                    loadTemplates();
                    showToast("Outreach created successfully");
                }}
            />
            <SequenceCreatorModal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                globalTemplate={globalTemplate}
                orgId={orgId}
                recruiterId={recruiterId}
                editMode={true}
                editSequence={selected}
                editSteps={(steps || []).map(step => {
                    const tmpl = (templates || []).find(t => t.id === step.template_id);
                    const channel = step.is_call_step ? "call"
                        : step.template_id && step.whatsapp_template_id ? "both"
                        : step.whatsapp_template_id ? "whatsapp" : "email";
                    return {
                        channel,
                        subject: tmpl?.subject || "",
                        body: tmpl?.body || "",
                        whatsappTemplateId: step.whatsapp_template_id || null,
                        delayDays: step.delay_days,
                        sendAt: "09:00",
                        _stepId: step.id,
                        _templateId: step.template_id,
                        _whatsappTemplateId: step.whatsapp_template_id || null,
                        _isCallStep: !!step.is_call_step,
                    };
                })}
                onSave={(updatedSeq) => {
                    setSequences(prev => prev.map(s => s.id === updatedSeq.id ? updatedSeq : s));
                    setSelected(updatedSeq);
                    setShowEditModal(false);
                    loadTemplates();
                    showToast("Outreach edited successfully");
                }}
            />
        {deleteTarget && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                <div className="dark:bg-[#1B1524] bg-bgPrimary dark:border dark:border-gray-700/50 rounded-2xl shadow-xl p-6 max-w-sm w-full mx-4">
                    <h3 className="text-sm font-semibold dark:text-white text-textPrimary mb-2">Delete outreach?</h3>
                    {deleteTarget.pendingCount > 0 ? (
                        <p className="text-sm dark:text-gray-400 text-textSecondary">
                            <span className="font-medium text-red-500 dark:text-red-400">{deleteTarget.pendingCount} scheduled email{deleteTarget.pendingCount > 1 ? "s" : ""}</span> in this outreach {`haven't`} been sent yet — they{`'ll`} be cancelled. This can{`'t`} be undone.
                        </p>
                    ) : (
                        <p className="text-sm dark:text-gray-400 text-textSecondary">This outreach will be removed from your list. This can{`'t`} be undone.</p>
                    )}
                    <div className="flex justify-end gap-2 mt-5">
                        <button onClick={() => setDeleteTarget(null)} className="text-xs px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 dark:text-[#CCCCCC] text-textSecondary hover:bg-gray-50 dark:hover:bg-white/10">Cancel</button>
                        <button onClick={confirmDelete} disabled={deleteLoading} className="text-xs px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-50">
                            {deleteLoading ? "Deleting…" : "Delete"}
                        </button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

export default SequenceManager;
